import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Save, Pencil, X as XIcon, ChevronDown, ChevronRight,
  Target, Users as UsersIcon, GraduationCap,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";

const LEVELS = ["D1 High Major", "D1 Mid Major +", "D1 Mid Major", "D1 Low Major", "NCAA D2", "D3 High Academic", "NCAA D3", "JUCO", "NAIA", "PREP"];

// Category display metadata. Keys must match backend contract (impact/role/development).
const CATEGORIES = [
  {
    key: "impact",
    heading: "Compete for Shots",
    subheading: "Impact",
    icon: Target,
    accent: "#22c55e",           // green — highest ceiling
    hint: "In first 2 years carve out a starting role.",
    testKey: "impact",
  },
  {
    key: "role",
    heading: "Compete for Minutes",
    subheading: "Fight for Role",
    icon: UsersIcon,
    accent: "var(--org-accent)", // gold — realistic target
    hint: "In first 2 years have consistent minutes.",
    testKey: "role",
  },
  {
    key: "development",
    heading: "Compete for Court",
    subheading: "Development / Network",
    icon: GraduationCap,
    accent: "#8ec5ff",           // blue — development / networking landing spot
    hint: "Have a roster spot and continue to develop / network.",
    testKey: "development",
  },
];

const EMPTY_CATEGORY = { levels: [], teams: "" };
const EMPTY_ALL = { impact: EMPTY_CATEGORY, role: EMPTY_CATEGORY, development: EMPTY_CATEGORY };

export default function RecruitmentAspirations({ playerId }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState(EMPTY_ALL);
  const [draft, setDraft] = useState(EMPTY_ALL);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getRecruitmentAspirations(playerId)
      .then(res => {
        if (cancelled) return;
        const cats = res?.categories || {};
        const merged = {
          impact:      cats.impact      || EMPTY_CATEGORY,
          role:        cats.role        || EMPTY_CATEGORY,
          development: cats.development || EMPTY_CATEGORY,
        };
        setData(merged);
        setDraft(merged);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [playerId]);

  const filled = useMemo(() => {
    return CATEGORIES.filter(c => (data[c.key]?.levels?.length || 0) > 0 || (data[c.key]?.teams || "").trim() !== "").length;
  }, [data]);

  const beginEdit = () => { setDraft(data); setEditMode(true); setSectionOpen(true); };
  const cancelEdit = () => { setDraft(data); setEditMode(false); };

  const toggleLevel = (catKey, level) => {
    setDraft(d => {
      const cur = d[catKey] || EMPTY_CATEGORY;
      const has = cur.levels.includes(level);
      return {
        ...d,
        [catKey]: { ...cur, levels: has ? cur.levels.filter(x => x !== level) : [...cur.levels, level] },
      };
    });
  };
  const setTeams = (catKey, teams) => {
    setDraft(d => ({ ...d, [catKey]: { ...(d[catKey] || EMPTY_CATEGORY), teams } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.saveRecruitmentAspirations(playerId, draft);
      const cats = res?.categories || draft;
      setData(cats);
      setDraft(cats);
      setEditMode(false);
      toast.success("Target levels saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <section className="border border-white/10 bg-[#171717]" data-testid="recruitment-aspirations">
      <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-white/10 flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setSectionOpen(v => !v)}
          className="flex items-center gap-3 text-left flex-1 min-w-0 group"
          aria-expanded={sectionOpen}
          data-testid="recruitment-aspirations-section-toggle"
        >
          {sectionOpen
            ? <ChevronDown className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0" />
            : <ChevronRight className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0" />}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Target Play Levels</div>
            <div className="font-heading text-xl sm:text-2xl uppercase tracking-tight text-white truncate">
              Where They Compete
              {filled > 0 && (
                <span className="ml-2 text-[11px] font-normal tracking-[0.2em] text-[#757575] align-middle">· {filled}/3 filled</span>
              )}
            </div>
          </div>
        </button>
        {canEdit && !editMode && (
          <button
            onClick={beginEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold border border-white/15 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
            data-testid="recruitment-aspirations-edit"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}
      </div>

      {sectionOpen && (
        <div className="p-4 sm:p-5">
          {loading ? (
            <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {CATEGORIES.map(cat => (
                  <CategoryCard
                    key={cat.key}
                    cat={cat}
                    editMode={editMode}
                    value={editMode ? (draft[cat.key] || EMPTY_CATEGORY) : (data[cat.key] || EMPTY_CATEGORY)}
                    onToggleLevel={(lv) => toggleLevel(cat.key, lv)}
                    onChangeTeams={(t) => setTeams(cat.key, t)}
                  />
                ))}
              </div>
              {editMode && (
                <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-white/10">
                  <button
                    onClick={cancelEdit}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#a0a0a0] hover:text-white"
                    data-testid="recruitment-aspirations-cancel"
                  >
                    <XIcon className="w-3 h-3 inline mr-0.5" /> Cancel
                  </button>
                  <button
                    onClick={save}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#121212] disabled:opacity-50"
                    style={{ background: "var(--org-accent)" }}
                    data-testid="recruitment-aspirations-save"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function CategoryCard({ cat, editMode, value, onToggleLevel, onChangeTeams }) {
  const Icon = cat.icon;
  const levels = value?.levels || [];
  const teams = value?.teams || "";
  const hasContent = levels.length > 0 || teams.trim() !== "";
  return (
    <div
      className="border bg-[#161616] p-4 flex flex-col"
      style={{ borderColor: `${cat.accent === "var(--org-accent)" ? "rgba(216,184,84,0.35)" : `${cat.accent}55`}` }}
      data-testid={`aspiration-card-${cat.testKey}`}
    >
      <div className="flex items-start gap-2 mb-1">
        <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: cat.accent }} />
        <div className="min-w-0">
          <div className="font-heading text-base uppercase tracking-tight text-white leading-tight">{cat.heading}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: cat.accent }}>
            {cat.subheading}
          </div>
        </div>
      </div>
      <div className="text-[11px] italic text-[#757575] mb-3">{cat.hint}</div>

      {/* Levels */}
      <div className="mb-3">
        <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5">Levels</div>
        {editMode ? (
          <div className="flex flex-wrap gap-1.5">
            {[...LEVELS, ...levels.filter(l => !LEVELS.includes(l))].map(lv => {
              const active = levels.includes(lv);
              return (
                <button
                  key={lv}
                  type="button"
                  onClick={() => onToggleLevel(lv)}
                  className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold border"
                  style={active
                    ? { color: "#121212", background: cat.accent, borderColor: cat.accent }
                    : { color: "#c0c0c0", background: "#0f0f0f", borderColor: "#333" }}
                  data-testid={`aspiration-${cat.testKey}-level-${lv.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  {lv}
                </button>
              );
            })}
          </div>
        ) : levels.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {levels.map(lv => (
              <span
                key={lv}
                className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                style={{ color: "#121212", background: cat.accent }}
              >
                {lv}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-[11px] italic text-[#575757]">No levels set</div>
        )}
      </div>

      {/* Teams */}
      <div className="flex-1">
        <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5">Example Teams</div>
        {editMode ? (
          <textarea
            rows={3}
            value={teams}
            onChange={(e) => onChangeTeams(e.target.value)}
            placeholder="e.g. Duke, UNC, Villanova"
            className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none"
            style={{ borderColor: teams ? `${cat.accent === "var(--org-accent)" ? "rgba(216,184,84,0.35)" : `${cat.accent}55`}` : undefined }}
            data-testid={`aspiration-${cat.testKey}-teams-input`}
          />
        ) : teams ? (
          <div className="text-[13px] text-[#c0c0c0] whitespace-pre-wrap break-words">{teams}</div>
        ) : (
          <div className="text-[11px] italic text-[#575757]">No teams listed</div>
        )}
      </div>

      {!editMode && !hasContent && (
        <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#575757] text-center border-t border-white/5 pt-2">
          Empty — click Edit above to fill in
        </div>
      )}
    </div>
  );
}
